import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  IAddHospitalRequest,
  IAddTenantRequest,
  IHospital,
  ISyncTenantIdsResult,
  ITenant,
  IUpdateTenantRequest,
} from "../../../@types/admin/AdminHospital";
import { hospitalsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export function useAddHospital() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    IHospital,
    Error,
    IAddHospitalRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await hospitalsService.addHospital(request)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-hospitals"] });
    },
  });

  return { addHospital: mutateAsync, isAddingHospital: isPending };
}

/**
 * Bulk Cognito write over one hospital group's users. Nothing to invalidate —
 * it changes Cognito, not anything this page reads — so the caller reports
 * the returned counts instead.
 */
export function useSyncTenantIds() {
  const { mutateAsync, isPending } = useMutation<
    ISyncTenantIdsResult,
    Error,
    number
  >({
    mutationFn: async (hospitalGroupId) =>
      ensureOk(await hospitalsService.syncTenantIds(hospitalGroupId)),
  });

  return { syncTenantIds: mutateAsync, isSyncingTenantIds: isPending };
}

export function useAddTenant() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ITenant,
    Error,
    IAddTenantRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await hospitalsService.addTenant(request)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
    },
  });

  return { addTenant: mutateAsync, isAddingTenant: isPending };
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ITenant,
    Error,
    IUpdateTenantRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await hospitalsService.updateTenant(request)),
    onSuccess: () => {
      // The hospital list names its tenants from this catalogue, so a rename
      // has to invalidate it too.
      queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
      queryClient.invalidateQueries({ queryKey: ["admin-hospitals"] });
    },
  });

  return { updateTenant: mutateAsync, isUpdatingTenant: isPending };
}

/** Invalidates everything the tenant detail page shows for one tenant. */
function useTenantInvalidation() {
  const queryClient = useQueryClient();

  return (tenantId: string) => {
    queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
    queryClient.invalidateQueries({
      queryKey: ["admin-tenant-details", tenantId],
    });
  };
}

export function useUpdateTenantStyles() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ITenant,
    Error,
    { tenantId: string; styles: Record<string, string> }
  >({
    mutationFn: async ({ tenantId, styles }) =>
      ensureOk(
        await hospitalsService.updateTenantStyles({
          tenantId,
          // The API stores the object as a JSON string, as Retool sent it.
          tenantStyles: JSON.stringify(styles),
        })
      ),
    onSuccess: (_data, { tenantId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-tenant-styles", tenantId],
      });
    },
  });

  return { updateTenantStyles: mutateAsync, isUpdatingStyles: isPending };
}

export function useUploadTenantLogo() {
  const invalidate = useTenantInvalidation();

  const { mutateAsync, isPending } = useMutation<
    void,
    Error,
    { tenantId: string; image: File }
  >({
    mutationFn: async ({ tenantId, image }) =>
      ensureOk(await hospitalsService.uploadTenantLogo(tenantId, image)),
    onSuccess: (_data, { tenantId }) => invalidate(tenantId),
  });

  return { uploadTenantLogo: mutateAsync, isUploadingLogo: isPending };
}

export function useDeleteTenantLogo() {
  const invalidate = useTenantInvalidation();

  const { mutateAsync, isPending } = useMutation<void, Error, string>({
    mutationFn: async (tenantId) =>
      ensureOk(await hospitalsService.deleteTenantLogo(tenantId)),
    onSuccess: (_data, tenantId) => invalidate(tenantId),
  });

  return { deleteTenantLogo: mutateAsync, isDeletingLogo: isPending };
}
